// frontend/src/pages/CobrancasPix.js
//
// Página "Cobranças › Pix Santander › Pix" (ETAPA 2: somente leitura).
// Lista as recorrências do Pix Automático lendo a ponte /api/pix do Souli. Carrega até 200 recorrências de
// uma vez e filtra no navegador (busca por cliente, CPF/CNPJ, descrição ou ID do Santander; filtro por status).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Search, AlertCircle, X } from 'lucide-react';
import { pixGet } from '../services/pixApi';
import PixRecurrenceActions from '../components/PixRecurrenceActions';
import { useAuth } from '../contexts/AuthContext';
import './Cobrancas.css';
import './CobrancasPix.css';

const STATUS_INFO = {
  ACTIVE: ['Ativa', 'ok'], ATIVA: ['Ativa', 'ok'], APROVADA: ['Aprovada', 'ok'],
  PENDING: ['Pendente', 'info'], PENDENTE: ['Pendente', 'info'], CRIADA: ['Criada (aguardando QR)', 'info'],
  PAUSED: ['Pausada', 'warn'], EXPIRADA: ['Expirada', 'warn'],
  COMPLETED: ['Concluída', 'done'], CONCLUIDA: ['Concluída', 'done'],
  CANCELED: ['Cancelada', 'bad'], CANCELADA: ['Cancelada', 'bad'], REJEITADA: ['Rejeitada', 'bad'],
};

const JORNADAS = {
  1: 'Jornada 1 (Push)',
  2: 'Jornada 2 (QR dinâmico)',
  3: 'Jornada 3 (QR + cobrança)',
  4: 'Jornada 4 (QR + vencimento)',
};

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '');
const normalizeStatus = (status) => String(status || 'DESCONHECIDO').toUpperCase();
const normalizeText = (value) => String(value == null ? '' : value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const onlyDigits = (value) => String(value == null ? '' : value).replace(/\D/g, '');

/** Rótulo e tom de cor de um status do Pix (desconhecidos aparecem como vieram, em tom neutro). */
function statusInfo(status) {
  const key = normalizeStatus(status);
  const found = STATUS_INFO[key];
  return found ? { label: found[0], tone: found[1] } : { label: capitalize(key), tone: 'neutral' };
}

function formatCurrency(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
}

/** Datas "só dia" (início da recorrência) são lidas em UTC para não recuar um dia no fuso do Brasil. */
function formatDate(value, { utc = false } = {}) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR', utc ? { timeZone: 'UTC' } : undefined);
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR');
}

function formatDocument(value) {
  const digits = onlyDigits(value);
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return value || '—';
}

/** Observações do detalhe: só aparecem se houver algo a dizer (senão a linha some). */
function observacoes(item) {
  const meta = (item && item.metadata) || {};
  const notas = [];
  if (meta.qrExpirado) notas.push('QR expirado');
  if (meta.primeiroPagamentoRecebido) notas.push('Primeiro pagamento recebido');
  return notas.length ? notas.join(' · ') : null;
}

/** Converte o erro da chamada em um texto claro para o usuário. */
function describeError(error) {
  const message = error && error.message;
  switch (error && error.code) {
    case 'PIX_ACCESS_DENIED':
      return { title: 'Sem permissão para o Pix', text: 'Seu usuário não tem acesso às cobranças Pix. Peça a um administrador a permissão de Cobranças.', retry: false };
    case 'PIX_NOT_CONFIGURED':
      return { title: 'Integração ainda não configurada', text: 'O servidor do Souli ainda não tem os dados de acesso ao sistema Pix. Avise o administrador.', retry: false };
    case 'PIX_LOGIN_FAILED':
    case 'PIX_AUTH_REJECTED':
      return { title: 'O Pix recusou a integração', text: 'A credencial que o Souli usa para falar com o Pix foi recusada. Avise o administrador.', retry: true };
    case 'PIX_TIMEOUT':
      return { title: 'O Pix demorou para responder', text: 'Tente novamente em instantes.', retry: true };
    case 'PIX_UNREACHABLE':
      return { title: 'Não foi possível conectar ao Pix', text: 'O sistema Pix parece estar fora do ar. Tente novamente em instantes.', retry: true };
    case 'PIX_ACCESS_CHECK_FAILED':
      return { title: 'Não consegui verificar suas permissões', text: message, retry: true };
    case 'NETWORK':
      return { title: 'Sem conexão', text: message, retry: true };
    default:
      return { title: 'Não foi possível carregar o Pix', text: message || 'Erro inesperado.', retry: true };
  }
}

function StatusBadge({ status }) {
  const { label, tone } = statusInfo(status);
  return <span className={`pix-badge pix-badge-${tone}`}>{label}</span>;
}

function DetailRow({ label, children }) {
  if (children === undefined || children === null || children === '') return null;
  return (
    <div className="pix-detalhe-linha">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export default function CobrancasPix() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [phase, setPhase] = useState('loading'); // loading | ok | error
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('TODOS');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setPhase('loading');
    setError(null);
    try {
      const body = await pixGet('/recurrences', { limit: 200, page: 1 });
      const list = Array.isArray(body && body.data) ? body.data : [];
      setItems(list);
      setTotal((body && body.pagination && body.pagination.totalCount) || list.length);
      setPhase('ok');
      pixGet('/health').then(setHealth).catch(() => setHealth({ authenticated: false, message: 'sem resposta' }));
    } catch (err) {
      setItems([]);
      setError(err);
      setPhase('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const statusOptions = useMemo(() => [...new Set(items.map((item) => normalizeStatus(item.status)))].sort(), [items]);

  const counts = useMemo(() => {
    const result = {};
    items.forEach((item) => { const key = normalizeStatus(item.status); result[key] = (result[key] || 0) + 1; });
    return result;
  }, [items]);

  const filtered = useMemo(() => {
    const term = normalizeText(search.trim());
    const termDigits = onlyDigits(search);
    return items.filter((item) => {
      if (statusFilter !== 'TODOS' && normalizeStatus(item.status) !== statusFilter) return false;
      if (!term) return true;
      const client = item.client || {};
      return normalizeText(client.name).includes(term)
        || normalizeText(item.description).includes(term)
        || normalizeText(item.santanderRecId).includes(term)
        || (termDigits.length >= 3 && onlyDigits(client.document).includes(termDigits));
    });
  }, [items, search, statusFilter]);

  const errorText = error ? describeError(error) : null;
  const connection = health === null
    ? { cls: 'wait', text: 'Verificando conexão com o Pix…' }
    : health.authenticated
      ? { cls: 'ok', text: `Pix conectado${health.latencyMs ? ` (${health.latencyMs} ms)` : ''}` }
      : { cls: 'off', text: `Pix sem conexão${health.message ? `: ${health.message}` : ''}` };

  return (
    <div className="cobrancas-container pix-pagina">
      <div className="cobrancas-header">
        <h1>Pix Santander</h1>
        {phase === 'ok' && <span className={`pix-conexao ${connection.cls}`}>● {connection.text}</span>}
        <button type="button" className="pix-btn" onClick={() => navigate('/cobrancas')}>
          <ArrowLeft size={16} /> Voltar
        </button>
      </div>

      {phase === 'loading' && (
        <div className="cobrancas-loading"><RefreshCw size={22} className="pix-girando" /><span>Carregando recorrências do Pix…</span></div>
      )}

      {phase === 'error' && errorText && (
        <div className="cobrancas-error" role="alert">
          <AlertCircle size={28} />
          <strong>{errorText.title}</strong>
          <span>{errorText.text}</span>
          {errorText.retry && <button type="button" className="pix-btn" onClick={load}><RefreshCw size={16} /> Tentar de novo</button>}
        </div>
      )}

      {phase === 'ok' && (
        <>
          <div className="cobrancas-filters-info">
            <span className="filter-chip">Total: {items.length}</span>
            {Object.entries(counts).map(([key, count]) => (
              <span key={key} className="filter-chip">{statusInfo(key).label}: {count}</span>
            ))}
          </div>

          <div className="pix-toolbar">
            <div className="pix-busca">
              <Search size={16} />
              <input
                type="search"
                aria-label="Buscar recorrência"
                placeholder="Buscar por cliente, CPF/CNPJ, descrição ou ID do Santander"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <select className="pix-select" aria-label="Filtrar por status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="TODOS">Todos os status</option>
              {statusOptions.map((key) => <option key={key} value={key}>{statusInfo(key).label}</option>)}
            </select>
            <button type="button" className="pix-btn" onClick={load}><RefreshCw size={16} /> Atualizar</button>
          </div>

          {total > items.length && (
            <p className="pix-aviso">Mostrando as {items.length} recorrências mais recentes de {total}.</p>
          )}

          {filtered.length === 0 ? (
            <div className="cobrancas-empty">
              <span>{items.length === 0 ? 'Nenhuma recorrência no Pix.' : 'Nenhuma recorrência encontrada com esse filtro.'}</span>
            </div>
          ) : (
            <div className="pix-tabela-wrap">
              <table className="pix-tabela">
                <thead>
                  <tr>
                    <th>Cliente</th><th>Descrição</th><th>Valor</th><th>Status</th><th>Periodicidade</th><th>Início</th><th>Jornada</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => (
                    <tr
                      key={item.id}
                      className="pix-linha"
                      tabIndex={0}
                      onClick={() => setSelected(item)}
                      onKeyDown={(event) => { if (event.key === 'Enter') setSelected(item); }}
                    >
                      <td className="pix-cliente">{(item.client && item.client.name) || 'Cliente removido'}</td>
                      <td>{item.description || '—'}</td>
                      <td className="pix-valor">{formatCurrency(item.amount)}</td>
                      <td><StatusBadge status={item.status} /></td>
                      <td>{capitalize(String(item.periodicity || '').toLowerCase()) || '—'}</td>
                      <td>{formatDate(item.startDate, { utc: true })}</td>
                      <td>{JORNADAS[item.jornada] || (item.jornada ? `Jornada ${item.jornada}` : '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {selected && (
        <div className="pix-modal-fundo" onClick={() => setSelected(null)}>
          <div className="pix-modal" role="dialog" aria-modal="true" aria-label="Detalhes da recorrência" onClick={(event) => event.stopPropagation()}>
            <div className="pix-modal-topo">
              <h2>Detalhes da recorrência</h2>
              <button type="button" className="pix-modal-fechar" aria-label="Fechar" onClick={() => setSelected(null)}><X size={18} /></button>
            </div>
            <dl className="pix-detalhes">
              <DetailRow label="Cliente">{(selected.client && selected.client.name) || 'Cliente removido'}</DetailRow>
              <DetailRow label="CPF/CNPJ">{selected.client && selected.client.document ? formatDocument(selected.client.document) : null}</DetailRow>
              <DetailRow label="Telefone">{selected.client && selected.client.phone}</DetailRow>
              <DetailRow label="E-mail">{selected.client && selected.client.email}</DetailRow>
              <DetailRow label="Descrição">{selected.description}</DetailRow>
              <DetailRow label="Valor">{formatCurrency(selected.amount)}</DetailRow>
              <DetailRow label="Status"><StatusBadge status={selected.status} /></DetailRow>
              <DetailRow label="Periodicidade">{selected.periodicity ? capitalize(String(selected.periodicity).toLowerCase()) : null}</DetailRow>
              <DetailRow label="Início">{formatDate(selected.startDate, { utc: true })}</DetailRow>
              <DetailRow label="Criada em">{formatDateTime(selected.createdAt)}</DetailRow>
              <DetailRow label="ID no Santander">{selected.santanderRecId}</DetailRow>
              <DetailRow label="Jornada">{JORNADAS[selected.jornada] || (selected.jornada ? `Jornada ${selected.jornada}` : null)}</DetailRow>
              <DetailRow label="TXID da cobrança">{selected.metadata && selected.metadata.txidCobranca}</DetailRow>
              <DetailRow label="Observações">{observacoes(selected)}</DetailRow>
            </dl>
            <PixRecurrenceActions recurrence={selected} user={authUser} onChanged={() => { setSelected(null); load(); }} />
          </div>
        </div>
      )}
    </div>
  );
}
